import React, { useEffect, useState } from 'react';
import {
  GetInterventionCostsResponse,
  GetInterventionLadderResponse,
  GetInterventionLengthsResponse,
  GetInterventionSuppliersResponse,
  InterventionLadderPeriod,
  InterventionLadderStepModel,
  InterventionLengthCourses,
} from '../../../api-service/clients';
import { moduleClientFactory } from '../../../api-service/module-client-factory';
import { today } from '../../../utilities';
import { InterventionCostsComponent } from '../intervention-costs/intervention-costs-component';
import { InterventionLengthsComponent } from '../intervention-lengths/intervention-lengths-component';
import { InterventionMovesComponent } from '../intervention-moves/intervention-moves-component';
import { InterventionSuppliersComponent } from '../intervention-suppliers/intervention-suppliers-component';
import { formatPeriod } from './intervention-ladder-caption';
import { InterventionLadderComponent, InterventionLadderDetails } from './intervention-ladder-component';

const interventionLadderClient = moduleClientFactory.createManagementInformationInterventionLadderClient();

interface SelectedStep {
  step: InterventionLadderStepModel;
  intensity: number;
}

interface StepLengths extends SelectedStep {
  courses: InterventionLengthCourses;
  lengths: GetInterventionLengthsResponse;
}

function lengthsKey(step: number, courses: InterventionLengthCourses) {
  return `${step}-${courses}`;
}

interface StepSuppliers {
  key: string;
  period: InterventionLadderPeriod;
  suppliers: GetInterventionSuppliersResponse;
}

function suppliersKey(step: number, period: InterventionLadderPeriod) {
  return `${step}-${period}`;
}

export function InterventionLadder() {
  const [period, setPeriod] = useState(InterventionLadderPeriod.Last12Months);
  const [ladder, setLadder] = useState<GetInterventionLadderResponse>();
  const [currentYear, setCurrentYear] = useState(today().getFullYear());
  const [hasFailed, setHasFailed] = useState(false);
  const [selectedStep, setSelectedStep] = useState<SelectedStep>();
  const [details, setDetails] = useState<InterventionLadderDetails>('moves');
  const [courses, setCourses] = useState(InterventionLengthCourses.Active);
  const [lengthsByKey, setLengthsByKey] = useState<Record<string, StepLengths>>({});
  const [shownLengths, setShownLengths] = useState<StepLengths>();
  const [hasLengthsFailed, setHasLengthsFailed] = useState(false);
  const [costsByStep, setCostsByStep] = useState<Record<number, GetInterventionCostsResponse>>({});
  const [shownCosts, setShownCosts] = useState<GetInterventionCostsResponse>();
  const [hasCostsFailed, setHasCostsFailed] = useState(false);
  const [suppliersByKey, setSuppliersByKey] = useState<Record<string, StepSuppliers>>({});
  const [shownSuppliers, setShownSuppliers] = useState<StepSuppliers>();
  const [hasSuppliersFailed, setHasSuppliersFailed] = useState(false);

  useEffect(() => {
    let isCurrent = true;
    interventionLadderClient
      .getInterventionLadder({ period })
      .then(response => {
        if (isCurrent) {
          setLadder(response);
          setCurrentYear(response.currentYear);
          setSelectedStep(current => current ?? (response.steps.length > 0 ? { step: response.steps[0], intensity: 0 } : undefined));
        }
      })
      .catch(() => isCurrent && setHasFailed(true));
    return () => {
      isCurrent = false;
    };
  }, [period]);

  const selectedStepNumber = selectedStep?.step.step;
  const isShowingSelectedStep = shownLengths !== undefined && shownLengths.step.step === selectedStepNumber && shownLengths.courses === courses;

  useEffect(() => {
    if (selectedStep === undefined || details !== 'moves' || isShowingSelectedStep) {
      return undefined;
    }
    let isCurrent = true;
    interventionLadderClient
      .getInterventionLengths({ step: selectedStep.step.step, courses })
      .then(response => {
        const stepLengths = { ...selectedStep, courses, lengths: response };
        setLengthsByKey(current => ({ ...current, [lengthsKey(response.step, courses)]: stepLengths }));
        if (isCurrent) {
          setShownLengths(stepLengths);
        }
      })
      .catch(() => isCurrent && setHasLengthsFailed(true));
    return () => {
      isCurrent = false;
    };
  }, [selectedStep, details, courses, isShowingSelectedStep]);

  const isShowingSelectedCosts = shownCosts !== undefined && shownCosts.step === selectedStepNumber;

  useEffect(() => {
    if (selectedStepNumber === undefined || details !== 'costs' || hasCostsFailed || isShowingSelectedCosts) {
      return undefined;
    }
    let isCurrent = true;
    interventionLadderClient
      .getInterventionCosts({ step: selectedStepNumber })
      .then(response => {
        setCostsByStep(current => ({ ...current, [response.step]: response }));
        if (isCurrent) {
          setShownCosts(response);
        }
      })
      .catch(() => isCurrent && setHasCostsFailed(true));
    return () => {
      isCurrent = false;
    };
  }, [selectedStepNumber, details, hasCostsFailed, isShowingSelectedCosts]);

  const isShowingSelectedSuppliers = selectedStepNumber !== undefined && shownSuppliers?.key === suppliersKey(selectedStepNumber, period);

  useEffect(() => {
    if (selectedStepNumber === undefined || details !== 'suppliers' || hasSuppliersFailed || isShowingSelectedSuppliers) {
      return undefined;
    }
    let isCurrent = true;
    interventionLadderClient
      .getInterventionSuppliers({ step: selectedStepNumber, period })
      .then(response => {
        const stepSuppliers = { key: suppliersKey(response.step, period), period, suppliers: response };
        setSuppliersByKey(current => ({ ...current, [stepSuppliers.key]: stepSuppliers }));
        if (isCurrent) {
          setShownSuppliers(stepSuppliers);
        }
      })
      .catch(() => isCurrent && setHasSuppliersFailed(true));
    return () => {
      isCurrent = false;
    };
  }, [selectedStepNumber, period, details, hasSuppliersFailed, isShowingSelectedSuppliers]);

  function onSelectPeriod(selected: InterventionLadderPeriod) {
    setPeriod(selected);
    setLadder(undefined);
    setHasFailed(false);
    if (selectedStepNumber !== undefined) {
      showCachedSuppliers(selectedStepNumber, selected);
    }
  }

  function showCachedSuppliers(step: number, selectedPeriod: InterventionLadderPeriod) {
    setHasSuppliersFailed(false);
    const cached = suppliersByKey[suppliersKey(step, selectedPeriod)];
    if (cached !== undefined) {
      setShownSuppliers(cached);
    }
  }

  function onSelectStep(step: InterventionLadderStepModel, intensity: number, selectedDetails: InterventionLadderDetails = 'moves') {
    setDetails(selectedDetails);
    setHasCostsFailed(false);
    setHasSuppliersFailed(false);
    if (step.step === selectedStepNumber) {
      return;
    }
    setSelectedStep({ step, intensity });
    showCachedLengths(step.step, courses);
    showCachedCosts(step.step);
    showCachedSuppliers(step.step, period);
  }

  function showCachedCosts(step: number) {
    const cached = costsByStep[step];
    if (cached !== undefined) {
      setShownCosts(cached);
    }
  }

  function onSelectCourses(selected: InterventionLengthCourses) {
    setCourses(selected);
    if (selectedStepNumber !== undefined) {
      showCachedLengths(selectedStepNumber, selected);
    }
  }

  function showCachedLengths(step: number, selectedCourses: InterventionLengthCourses) {
    setHasLengthsFailed(false);
    const cached = lengthsByKey[lengthsKey(step, selectedCourses)];
    if (cached !== undefined) {
      setShownLengths(cached);
    }
  }

  // Until the selected step's lengths arrive, the previous step's stay in place so the section keeps its size.
  const shownStep = hasLengthsFailed ? selectedStep : shownLengths ?? selectedStep;
  const shownCostsStep = (!hasCostsFailed && ladder?.steps.find(step => step.step === shownCosts?.step)) || selectedStep?.step;
  const shownSuppliersStep = (!hasSuppliersFailed && ladder?.steps.find(step => step.step === shownSuppliers?.suppliers.step)) || selectedStep?.step;

  return (
    <>
      <InterventionLadderComponent
        period={period}
        currentYear={currentYear}
        ladder={ladder}
        hasFailed={hasFailed}
        selectedStep={selectedStepNumber}
        details={details}
        onSelectPeriod={onSelectPeriod}
        onSelectStep={onSelectStep}
      />
      {!hasFailed && details === 'moves' && selectedStepNumber !== undefined && (
        <InterventionMovesComponent steps={ladder?.steps} selectedStep={selectedStepNumber} periodLabel={formatPeriod(period, currentYear)} onSelectStep={onSelectStep} />
      )}
      {!hasFailed && details === 'costs' && shownCostsStep && (
        <InterventionCostsComponent
          step={shownCostsStep}
          costs={hasCostsFailed ? undefined : shownCosts}
          isLoading={!hasCostsFailed && !isShowingSelectedCosts}
          hasFailed={hasCostsFailed}
        />
      )}
      {!hasFailed && details === 'suppliers' && shownSuppliersStep && (
        <InterventionSuppliersComponent
          key={shownSuppliers?.key}
          step={shownSuppliersStep}
          periodLabel={formatPeriod(shownSuppliers?.period ?? period, currentYear)}
          suppliers={hasSuppliersFailed ? undefined : shownSuppliers?.suppliers}
          isLoading={!hasSuppliersFailed && !isShowingSelectedSuppliers}
          hasFailed={hasSuppliersFailed}
        />
      )}
      {details === 'moves' && shownStep && (
        <InterventionLengthsComponent
          step={shownStep.step}
          intensity={shownStep.intensity}
          courses={courses}
          lengths={hasLengthsFailed ? undefined : shownLengths?.lengths}
          isLoading={!hasLengthsFailed && !isShowingSelectedStep}
          hasFailed={hasLengthsFailed}
          onSelectCourses={onSelectCourses}
        />
      )}
    </>
  );
}
